/**
 * Analytics Page
 * This page is intended to empower the customer to uncover
 * insights and trends in their data, over longer periods of time.
 */
import { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { getStackDataByLocationId } from '../../utils/api';
import DropdownFranchise from '../../components/dropdowns/DropdownFranchise';
import PageLayout from '../../components/ui/PageLayout';
import StackGraph from '@/components/graphs/analytics/StackGraph';
import { type Stack } from '@/types/Stack';
import DropdownStacksAggregate from '@/components/dropdowns/DropdownStackAggregate';
import StackTable from '@/components/tables/StackTable';
import DriveOffTable from '@/components/tables/DriveOffTable';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import DatepickerWithDefaults from '@/components/time/DatepickerWIthDefaults';
import DropdownLocationSingle from '@/components/dropdowns/DropdownLocationSingle';
import { Toaster, toast } from 'sonner';


export default function StacksContainer(): JSX.Element {
  const today = new Date();
  const weekAgo = new Date();
  today.setDate(today.getDate()-1);
  weekAgo.setDate(weekAgo.getDate() - 7);
  weekAgo.setHours(0);

  const {
    selectedFranchise,
    selectedLocation,
    selectedDates,
    handleSelectedDates,
    setSelectedStackDriveOffFilter,
  } = useData();

  const [datesSet, setDatesSet] = useState(false);
  const [franchiseSet, setFranchiseSet] = useState(false);

  const [stackData, setStackData] = useState<Stack>();
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [isLoading, setIsLoading] = useState(true);



  // Using effect chain to ensure that these effects happen in order
  useEffect(() => {
    handleSelectedDates([weekAgo, today]);
    setDatesSet(true);
    setIsLoading(true);
    if (stackData?.bucketData !== undefined) {
    }
  }, []);

  useEffect(() => {
    if ((datesSet) || (selectedDates[0] == today)) {
      return;
    }
    setFranchiseSet(true);
  }, [selectedDates]);

  useEffect(() => {
    if (franchiseSet) {
      return;
    }
    setSelectedStackDriveOffFilter('30min');
  }, [selectedFranchise]);

  // Get day parts data when toggled or date or location or franchise changes.
  useEffect(() => {
    // skipping the first invocation if the date and franchise hasn't been set yet
    if (!(datesSet && franchiseSet)) {
      return;
    }
    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    async function getData(): Promise<void> {
      if (selectedFranchise === null || selectedLocation === null) {
        return;
      }
      setIsLoading(true);
      setStackData(undefined);
      try {
        const data = await getStackDataByLocationId(
          selectedFranchise.id,
          selectedDates[0],
          selectedDates[1] ?? selectedDates[0], // Use selectedDates[0] twice if selectedDates[1] is undefined
          [selectedLocation.id],
          { signal: newAbortController.signal } // pass the abort signal
        );
        setStackData(data);
        setIsLoading(false); // Reset loading state when done
      } catch (error) {
        if (error instanceof Error) { // Type guard to check if it's an Error
          if (error.name === 'AbortError') {
            console.error('Error fetching stack data:', error.message);
          } else {
            toast.error("There was an error fetching the stack data. Please try again.")
          }
        } else {
          console.error('Unexpected error:', error); // Handle unknown types
          toast.error("There was an error fetching the stack data. Please try again.")
          
        }
        
      } 
    }
    getData().catch((error) => {
      console.error(error);
    });
  }, [selectedDates, selectedLocation, selectedFranchise, franchiseSet]);

  // Handle date change
  function handleDateChange(dates: Date[]): void {
    handleSelectedDates(dates);
  }



  return (
    <PageLayout>
    <Toaster position="bottom-right" /> {/* Adjusted position for better visibility */}

      <div data-cy="stacks">
        <div className="sm:flex sm:items-centerm flex-col gap-4">
          {/* Selectors */}
          <div className="sm:flex sm:justify-between sm:items-center mb-8 gap-4">
            <DropdownFranchise />
            <DropdownLocationSingle />
            <DropdownStacksAggregate />
            <DatepickerWithDefaults
              initDates={selectedDates}
              handleDateChange={handleDateChange}
              mode='range'
            />

          </div>
          <StackGraph bucketData={stackData?.bucketData} isLoading={isLoading} />
           
          {/* Conditionally render the Carousel based on the URL host (TODO: need better way of doing this so that we separate beta from prod) */}

          <div className="px-6">
          <Carousel className="w-full">
            <CarouselContent>
              <CarouselItem>
                <StackTable stackData={stackData?.stackData} />
              </CarouselItem>
              <CarouselItem>
                <DriveOffTable driveOffsData={stackData?.driveOffData} />
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
          </div>
          

        </div>
      </div>
    </PageLayout>
  );
}
